/**
 * Permissions sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchPermissions,
  fetchPermission,
  createPermission,
  updatePermissionAction,
  deletePermission,
  fetchPermissionsByResource,
  setPermissions,
  setCurrentPermission,
  addPermission,
  updatePermission,
  removePermission,
  setLoading,
  setError,
} from './permissionsSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all permissions saga
 */
function* fetchPermissionsSaga(action: ReturnType<typeof fetchPermissions>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: Record<string, any> = {};
    if (action.payload) {
      const payload = action.payload as Record<string, any>;
      if (payload.resource) params.resource = payload.resource;
      if (payload.action) params.action = payload.action;
      if (payload.isActive !== undefined) params.isActive = payload.isActive;
    }
    const response = yield call(httpRequest.get, endPoints.permissions.getAll, { params });

    if (response?.success) {
      yield put(setPermissions(response.data || response));
    } else {
      toast.error('Failed to fetch permissions');
      yield put(setError('Failed to fetch permissions'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch permissions';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single permission saga
 */
function* fetchPermissionSaga(action: ReturnType<typeof fetchPermission>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.permissions.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentPermission(response.data || response));
    } else {
      toast.error('Failed to fetch permission');
      yield put(setError('Failed to fetch permission'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch permission';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create permission saga
 */
function* createPermissionSaga(action: ReturnType<typeof createPermission>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.permissions.create, action.payload);

    if (response?.success) {
      const newPermission = response.data || response;
      yield put(addPermission(newPermission));
      toast.success('Permission created successfully');
    } else {
      toast.error('Failed to create permission');
      yield put(setError('Failed to create permission'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to create permission';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update permission saga
 */
function* updatePermissionSaga(action: ReturnType<typeof updatePermissionAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.permissions.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedPermission = { ...response.data, _id: action.payload.id };
      yield put(updatePermission(updatedPermission));
      toast.success('Permission updated successfully');
    } else {
      toast.error('Failed to update permission');
      yield put(setError('Failed to update permission'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to update permission';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete permission saga
 */
function* deletePermissionSaga(action: ReturnType<typeof deletePermission>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.permissions.delete(action.payload));

    if (response?.success || response) {
      yield put(removePermission(action.payload));
      toast.success('Permission deleted successfully');
    } else {
      toast.error('Failed to delete permission');
      yield put(setError('Failed to delete permission'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to delete permission';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch permissions by resource saga
 */
function* fetchPermissionsByResourceSaga(action: ReturnType<typeof fetchPermissionsByResource>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.permissions.getByResource(action.payload));

    if (response?.success) {
      yield put(setPermissions(response.data || response));
    } else {
      toast.error('Failed to fetch permissions by resource');
      yield put(setError('Failed to fetch permissions by resource'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch permissions by resource';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

export function* permissionsSagas(): Generator<any, void, any> {
  yield takeLatest(fetchPermissions, fetchPermissionsSaga);
  yield takeLatest(fetchPermission, fetchPermissionSaga);
  yield takeLatest(createPermission, createPermissionSaga);
  yield takeLatest(updatePermissionAction, updatePermissionSaga);
  yield takeLatest(deletePermission, deletePermissionSaga);
  yield takeLatest(fetchPermissionsByResource, fetchPermissionsByResourceSaga);
}

